import type { ReactNode } from 'react'
import { useCallback } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { MANAGE_ALERTS_PATH } from 'constants/savedAlerts'

const SnackbarElement = styled.div`
  width: 100%;
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: ${props => props.theme.shadow.bottom.large};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

export interface AlertConfirmationSnackbarContent {
  /** Leading icon, typically a bell for an alert that was added and a struck bell for one removed. */
  icon: ReactNode
  heading: string
  description: string
  manageAlertsLabel: string
  confirmLabel: string
  /** Runs before the snackbar closes, used by the undo action. */
  onConfirm?: () => void
}

interface Props extends AlertConfirmationSnackbarContent {
  onClose: () => void
}

/**
 * Confirmation shown after an alert is added or removed, on every alert surface — destination
 * watchlist and flight price alerts alike. The "Manage alerts" action is a link rather than a
 * dispatch so it picks up the region prefix and the `#saved-alerts` hash.
 */
function AlertConfirmationSnackbar(props: Props) {
  const { icon, heading, description, manageAlertsLabel, confirmLabel, onClose, onConfirm = noop } = props

  const handleConfirm = useCallback(() => {
    onConfirm()
    onClose()
  }, [onConfirm, onClose])

  return (
    <SnackbarElement role="alert">
      <Group direction="vertical" gap={16}>
        <Group direction="horizontal" gap={16} verticalAlign="start">
          {icon}
          <Heading variant="heading4">{heading}</Heading>
        </Group>
        <BodyText variant="large" colour="light-foreground-alt">{description}</BodyText>
        <Group direction="horizontal" gap={8} horizontalAlign="end" verticalAlign="center">
          <TextButton kind="tertiary" to={MANAGE_ALERTS_PATH} onClick={onClose}>{manageAlertsLabel}</TextButton>
          <TextButton kind="primary" onClick={handleConfirm}>{confirmLabel}</TextButton>
        </Group>
      </Group>
    </SnackbarElement>
  )
}

export function showAlertConfirmationSnackbar(
  content: AlertConfirmationSnackbarContent,
  kind: 'success' | 'neutral' = 'success',
) {
  showSnackbar(content.description, kind, {
    renderCustomContent: ({ onClose }) => <AlertConfirmationSnackbar {...content} onClose={onClose} />,
  })
}

export default AlertConfirmationSnackbar
